import CaretBlueUrl from '@/assets/svg/caret-blue.svg?raw';
import ChevronUrl from '@/assets/svg/chevron.svg?raw';
import { svgUriEncoder } from '@/utils';
import { css, unsafeCSS } from 'lit';

export const formStyles = css`
  :host([part='wholesale']) input,
  :host([part='wholesale']) select {
    font-size: 1rem;
    color: var(--fg-primary, var(--fg-fallback-primary));
    height: 48px;
    border: 1px solid var(--fg-gray--300);
    box-shadow: none;
    padding: 0 16px;
    transition: border-color ease-in-out 0.3s;
  }

  :host([part='wholesale']) input,
  :host([part='wholesale']) select {
    color: var(--fg-text, var(--fg-fallback-text));
    box-shadow: none;
    border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
  }

  :host([part='retail']) input:focus-visible {
    outline: 2px solid var(--fg-primary, var(--fg-fallback-primary));
    outline-offset: 2px;
  }

  :host([part='wholesale']) select {
    padding: 0 26px 0 16px;
  }

  :host([part='wholesale']) input:focus-visible,
  :host([part='wholesale']) select:focus-visible {
    outline: 2px solid var(--fg-secondary);
    outline-offset: 2px;
  }

  :host([part='wholesale']) input::placeholder,
  :host([part='wholesale']) input:disabled::placeholder,
  :host([part='wholesale']) textarea::placeholder {
    color: var(--fg-gray--500);
  }

  :host([part='wholesale']) input.invalid {
    animation-name: invalid-blink;
    animation-duration: 2s;
    animation-timing-function: ease-in-out;
  }

  :host([part='wholesale']) input.warning {
    border: 1px solid var(--fg-warning, var(--fg-fallback-warning));
  }

  :host([part='wholesale']) input.warning:focus-visible {
    outline: 2px solid var(--fg-warning, var(--fg-fallback-warning));
    outline-offset: 2px;
  }

  :host([part='wholesale']) input:focus-visible {
    outline: 2px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
    outline-offset: 2px;
  }

  label {
    cursor: pointer;
  }

  select,
  input[list] {
    appearance: none;
    background-image: url(${unsafeCSS(svgUriEncoder(ChevronUrl))});
    background-size: 20px;
    background-repeat: no-repeat;
    background-position: right 10px center;
    cursor: pointer;
  }

  :host select,
  :host input[list] {
    background-image: url(${unsafeCSS(svgUriEncoder(CaretBlueUrl))});
    background-size: 11px;
  }

  input,
  select {
    font-family: var(
      --fg-font-family--serif,
      var(--fg-fallback-font-family--serif)
    );
    font-size: 1rem;
    color: #000;
    height: 37px;
    border: 1px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
    border-radius: var(
      --fg-borderradius--xs,
      var(--fg-fallback-borderradius--xs)
    );
    box-shadow: 0px 2px 4px 0px #00000026 inset;
    padding: 0 10px;
  }

  input:disabled {
    background: var(--fg-gray--300, var(--fg-fallback-gray--300));
  }

  input:disabled::placeholder {
    color: var(--fg-gray--500, var(--fg-fallback-gray--500));
  }

  :host([part='retail']) input[type='radio'] {
    border: 2px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
    width: 20px;
    height: 20px;

    &[checked] {
      border: 6px solid
        var(--fg-config-selected, var(--fg-fallback-config-selected));
    }
  }

  :host([part='wholesale']) input[type='radio'] {
    border-radius: 12px;
    box-shadow: none;
    cursor: pointer;
    display: block;
    flex-shrink: 0;
    height: 24px;
    margin: 0;
    padding: 0;
    position: relative;
    width: 24px;

    &::before {
      content: '';
      display: block;
      width: 24px;
      height: 24px;
      background-color: transparent;
      border-radius: 12px;
      border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray-300));
      position: absolute;
      left: 0;
      bottom: 0;
      pointer-events: none;
    }

    &:checked::before,
    &:focus-visible::before {
      background-color: var(
        --fg-success--dark,
        var(--fg-fallback-success--dark)
      );
      border: 4px solid #ffffff;
      outline: 1px solid
        var(--fg-success--dark, var(--fg-fallback-success--dark));
      outline-offset: -1px;
      left: 0;
      bottom: 0;
      pointer-events: none;
    }
  }

  textarea {
    font-family: var(
      --fg-font-family--serif,
      var(--fg-fallback-font-family--serif)
    );
    border: 1px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
    border-radius: var(
      --fg-borderradius--xs,
      var(--fg-fallback-borderradius--xs)
    );
    font-size: 1rem;
    line-height: 1.1875rem;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.15);
    min-height: 88px;
    resize: none;
    padding: 10px;
    margin-bottom: 15px;
  }

  textarea:focus-visible {
    border: 1px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
    outline: 2px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
    outline-offset: 2px;
  }

  textarea::placeholder {
    color: var(--fg-gray--600, var(--fg-fallback-gray--600));
  }

  :host([part='wholesale']) textarea {
    border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
    line-height: 140%;
    box-shadow: none;
    padding: 0.75rem;
  }

  :host([part='wholesale']) textarea:focus-visible {
    border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
    outline: 2px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
    outline-offset: 2px;
  }

  @keyframes invalid-blink {
    0% {
      border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
    }
    50% {
      border: 1px solid var(--fg-error);
    }
    100% {
      border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
    }
  }
`;
